import { cn } from '@/lib/cn';
import { Figure } from './Figure';
import type { KpiItem } from './kpis';

/**
 * A faixa de indicadores do topo.
 *
 * Cinco números numa régua só, e não cinco cartões: eles são uma conta — disponível menos
 * planejado dá o saldo, e o saldo se reparte em ociosidade e sobrecarga. As linhas que
 * os separam são as de uma tabela, porque é isso que eles são. Os números vêm prontos de
 * `kpisFor`.
 */
/** Quantas células cabem lado a lado no monitor — cinco no painel, quatro no quantitativo. */
const COLUMNS = {
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
} as const;

export function KpiStrip({
  items,
  columns = 5,
  className,
}: {
  items: readonly KpiItem[];
  columns?: keyof typeof COLUMNS;
  className?: string;
}) {
  return (
    <dl
      className={cn(
        // As linhas da régua são o fundo aparecendo pelo vão de 1px entre as células —
        // funciona para qualquer número de colunas, no celular e no monitor.
        'grid grid-cols-2 gap-px overflow-hidden rounded-(--radius-card) border border-(--color-border-subtle) bg-(--color-border-subtle)',
        COLUMNS[columns],
        className,
      )}
    >
      {items.map((item, index) => (
        <div
          key={item.label}
          className={cn(
            'flex flex-col gap-1.5 bg-(--color-surface-raised) p-4',
            // Cinco números em duas colunas deixam um vão; o último ocupa a linha.
            index === items.length - 1 && items.length % 2 === 1 && 'col-span-2 lg:col-span-1',
          )}
        >
          <dt className="text-[11px] font-semibold uppercase tracking-[0.08em] text-(--color-content-muted)">
            {item.label}
          </dt>
          <dd className="flex flex-col gap-1">
            <Figure value={item.value} tone={item.tone ?? 'default'} />
            {item.hint !== undefined && (
              <span className="text-xs leading-snug text-(--color-content-muted)">{item.hint}</span>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}
